import { useCallback, useEffect, useState } from 'react';
import {
  TEMPLATE_KEYS,
  deleteTemplate,
  listTags,
  listTemplates,
  saveTemplate,
  type CameraTagWithCount,
  type CameraTemplate,
  type TemplateKey,
  type TemplateValues,
} from '../lib/api';
import { TagPicker } from './CameraTags';
import { DialogHapus } from './DialogHapus';
import { useBahasa } from '../i18n';
import { RETENSI_MAX_HARI } from '../lib/retensi';

/**
 * Configuration Template (§G11.4) — dikelola di `/admin` karena ini setelan
 * sistem, bukan urusan orang (`§C2.1`).
 *
 * **Tiap field punya saklarnya sendiri, dan itu bukan kerumitan yang bisa
 * dihindari.** Yang ditimpa saat template diterapkan hanya kunci yang ADA di
 * dalamnya, dan `maxDay: null` (ikut retensi global, §D6) adalah nilai yang
 * sah — jadi "tidak ditemplatkan" tidak bisa diwakili nilai apa pun. Tanpa
 * saklar, template berisi tiga parameter akan mengosongkan sembilan parameter
 * lain yang tidak ada hubungannya dengan alasan orang menekan tombolnya.
 */

type Jenis = 'teks' | 'angka' | 'bool' | 'transport' | 'tags';

const FIELD: Record<
  TemplateKey,
  { label: string; jenis: Jenis; bantuan?: string; maks?: number }
> = {
  transport: { label: 'RTSP transport', jenis: 'transport' },
  port: { label: 'RTSP port', jenis: 'angka' },
  httpPort: { label: 'Web UI port', jenis: 'angka' },
  httpSecure: { label: 'Web UI uses HTTPS', jenis: 'bool' },
  mainPath: { label: 'Main stream path', jenis: 'teks' },
  subPath: { label: 'Sub stream path', jenis: 'teks', bantuan: 'Empty means this camera has no sub stream.' },
  username: { label: 'Username', jenis: 'teks', bantuan: 'Empty means no username.' },
  audioEnabled: { label: 'Record audio', jenis: 'bool' },
  recordEnabled: { label: 'Record (View and Record)', jenis: 'bool' },
  // §D6 — batas 30 hari berlaku juga di sini. Template adalah jalur yang paling
  // mudah terlupakan saat sebuah aturan dipasang, dan nilainya diterapkan ke
  // banyak kamera sekaligus lewat Bulk Operation (§G11.2).
  maxDay: {
    label: 'Retention (days)',
    jenis: 'angka',
    maks: RETENSI_MAX_HARI,
    bantuan: 'Empty means it follows the global retention setting. At most 30 days.',
  },
  segmentSeconds: { label: 'Segment length (seconds)', jenis: 'angka', bantuan: 'Empty means it follows the global segment setting.' },
  tagIds: { label: 'Tags', jenis: 'tags' },
};

const BAWAAN: TemplateValues = {
  transport: 'tcp',
  port: 554,
  httpPort: 80,
  httpSecure: false,
  mainPath: '',
  subPath: null,
  username: null,
  audioEnabled: false,
  recordEnabled: true,
  maxDay: null,
  segmentSeconds: null,
  tagIds: [],
};

interface Draft {
  id: string | null;
  name: string;
  description: string;
  values: TemplateValues;
  /** Kunci yang saklarnya menyala — inilah yang menentukan isi JSON-nya. */
  aktif: Set<TemplateKey>;
  password: string;
  /** Password hanya ikut terkirim kalau kotaknya benar-benar disentuh. */
  passwordDisentuh: boolean;
  /** Template lama yang sudah punya kredensial tersimpan. */
  punyaPassword: boolean;
}

function draftBaru(): Draft {
  return {
    id: null,
    name: '',
    description: '',
    values: { ...BAWAAN },
    aktif: new Set(),
    password: '',
    passwordDisentuh: false,
    punyaPassword: false,
  };
}

function draftDari(tpl: CameraTemplate): Draft {
  return {
    id: tpl.id,
    name: tpl.name,
    description: tpl.description ?? '',
    values: { ...BAWAAN, ...tpl.values },
    aktif: new Set(Object.keys(tpl.values) as TemplateKey[]),
    password: '',
    passwordDisentuh: false,
    punyaPassword: tpl.hasPassword,
  };
}

/** Satu kalimat isi template, untuk daftar — tanpa membuka editornya. */
function ringkas(tpl: CameraTemplate, t: (s: string, v?: Record<string, string | number>) => string): string {
  const n = Object.keys(tpl.values).length;
  return tpl.hasPassword
    ? t('{n} settings · credentials stored', { n })
    : t('{n} settings', { n });
}

export function CameraTemplates({ bolehUbah }: { bolehUbah: boolean }) {
  const { t } = useBahasa();
  const [templates, setTemplates] = useState<CameraTemplate[]>([]);
  const [tags, setTags] = useState<CameraTagWithCount[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [galat, setGalat] = useState<string | null>(null);
  const [sibuk, setSibuk] = useState(false);
  const [akanDihapus, setAkanDihapus] = useState<CameraTemplate | null>(null);

  const muat = useCallback(async () => {
    try {
      const [tpl, tg] = await Promise.all([listTemplates(), listTags()]);
      setTemplates(tpl);
      setTags(tg);
      setGalat(null);
    } catch (e) {
      setGalat((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void muat();
  }, [muat]);

  function setNilai<K extends TemplateKey>(k: K, v: TemplateValues[K]) {
    setDraft((d) => (d ? { ...d, values: { ...d.values, [k]: v } } : d));
  }

  function toggleAktif(k: TemplateKey) {
    setDraft((d) => {
      if (!d) return d;
      const aktif = new Set(d.aktif);
      if (aktif.has(k)) aktif.delete(k);
      else aktif.add(k);
      return { ...d, aktif };
    });
  }

  async function simpan() {
    if (!draft) return;
    setSibuk(true);
    setGalat(null);
    try {
      // Hanya kunci yang saklarnya menyala yang masuk JSON — itulah seluruh
      // arti "kunci yang ada di template yang ditimpa" (§G11.4).
      const values: TemplateValues = {};
      for (const k of TEMPLATE_KEYS) {
        if (draft.aktif.has(k)) (values as Record<string, unknown>)[k] = draft.values[k];
      }
      await saveTemplate(draft.id, {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        values,
        ...(draft.passwordDisentuh ? { password: draft.password || null } : {}),
      });
      setDraft(null);
      await muat();
    } catch (e) {
      setGalat((e as Error).message);
    } finally {
      setSibuk(false);
    }
  }

  if (draft) {
    return (
      <div>
        {galat && (
          <p role="alert" className="mb-3 rounded-md border border-red-600 px-3 py-2 text-sm">
            {galat}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            {t('Template name')}
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              maxLength={128}
              placeholder="Kamera POP"
              className="mt-1 min-h-tap w-full rounded-md border border-line bg-page px-3 text-base"
            />
          </label>
          <label className="block text-sm">
            {t('Description')}
            <input
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              maxLength={255}
              className="mt-1 min-h-tap w-full rounded-md border border-line bg-page px-3 text-base"
            />
          </label>
        </div>

        <p className="mt-4 text-sm text-muted">
          {t('Tick a setting to put it in the template. Anything unticked is left alone when the template is applied.')}
        </p>

        <ul className="mt-2 divide-y divide-line border-y border-line">
          {TEMPLATE_KEYS.map((k) => {
            const f = FIELD[k];
            const on = draft.aktif.has(k);
            return (
              <li key={k} className="flex flex-wrap items-center gap-3 py-2">
                <label className="flex min-h-tap w-56 items-center gap-2 text-sm">
                  <input type="checkbox" checked={on} onChange={() => toggleAktif(k)} />
                  {t(f.label)}
                </label>

                <div className={`min-w-0 flex-1 ${on ? '' : 'pointer-events-none opacity-40'}`}>
                  {f.jenis === 'transport' && (
                    <select
                      value={draft.values.transport ?? 'tcp'}
                      onChange={(e) => setNilai('transport', e.target.value as 'tcp' | 'udp')}
                      className="min-h-tap rounded-md border border-line bg-page px-2 text-sm"
                    >
                      <option value="tcp">TCP</option>
                      <option value="udp">UDP</option>
                    </select>
                  )}

                  {f.jenis === 'bool' && (
                    <label className="flex min-h-tap items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={Boolean(draft.values[k])}
                        onChange={(e) =>
                          setNilai(k, e.target.checked as TemplateValues[typeof k])
                        }
                      />
                      {t('On')}
                    </label>
                  )}

                  {f.jenis === 'angka' && (
                    <input
                      type="number"
                      max={f.maks}
                      value={draft.values[k] === null || draft.values[k] === undefined ? '' : String(draft.values[k])}
                      onChange={(e) =>
                        setNilai(
                          k,
                          (e.target.value === ''
                            ? null
                            : Number(e.target.value)) as TemplateValues[typeof k],
                        )
                      }
                      className="min-h-tap w-32 rounded-md border border-line bg-page px-2 text-sm"
                    />
                  )}

                  {f.jenis === 'teks' && (
                    <input
                      value={(draft.values[k] as string | null) ?? ''}
                      onChange={(e) =>
                        setNilai(
                          k,
                          (e.target.value === '' ? null : e.target.value) as TemplateValues[typeof k],
                        )
                      }
                      className="min-h-tap w-full rounded-md border border-line bg-page px-2 text-sm"
                    />
                  )}

                  {f.jenis === 'tags' && (
                    <TagPicker
                      tags={tags}
                      terpilih={draft.values.tagIds ?? []}
                      onChange={(ids) => setNilai('tagIds', ids)}
                    />
                  )}

                  {f.bantuan && <p className="mt-0.5 text-xs text-muted">{t(f.bantuan)}</p>}
                </div>
              </li>
            );
          })}
        </ul>

        {/* Kredensial boleh masuk template, dan itu setengah kegunaannya: satu
            batch kamera dari vendor yang sama hampir selalu berbagi
            username/password. Yang ditampilkan cuma "tersimpan" atau "kosong",
            tidak pernah nilainya (§F5). */}
        <label className="mt-4 block text-sm">
          {t('Password')}
          <input
            type="password"
            value={draft.password}
            onChange={(e) =>
              setDraft({ ...draft, password: e.target.value, passwordDisentuh: true })
            }
            placeholder={draft.punyaPassword ? t('Stored — type to replace') : t('Not set')}
            className="mt-1 min-h-tap w-full rounded-md border border-line bg-page px-3 text-base sm:w-72"
          />
          <span className="mt-1 block text-xs text-muted">
            {t('Stored encrypted. It is never sent back to the browser.')}
          </span>
        </label>

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            disabled={sibuk || draft.name.trim() === ''}
            onClick={() => void simpan()}
            className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast disabled:opacity-60"
          >
            {sibuk ? t('Saving…') : t('Save')}
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft(null);
              setGalat(null);
            }}
            className="min-h-tap rounded-md border border-line px-4 text-base"
          >
            {t('Cancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {galat && (
        <p role="alert" className="mb-3 rounded-md border border-red-600 px-3 py-2 text-sm">
          {galat}
        </p>
      )}

      <p className="text-sm text-muted">
        {t('A template fills in a new camera form. It is a starting point, not a link — editing a template never changes cameras that already exist.')}
      </p>

      {templates.length === 0 ? (
        <p className="mt-4 text-sm text-muted">{t('No templates yet.')}</p>
      ) : (
        <ul className="mt-4 divide-y divide-line border-y border-line">
          {templates.map((tpl) => (
            <li key={tpl.id} className="flex flex-wrap items-center gap-2 py-2">
              <span className="min-w-0 flex-1">
                <span className="block text-sm">{tpl.name}</span>
                <span className="block text-xs text-muted">
                  {tpl.description ? `${tpl.description} · ` : ''}
                  {ringkas(tpl, t)}
                </span>
              </span>
              {bolehUbah && (
                <>
                  <button
                    type="button"
                    onClick={() => setDraft(draftDari(tpl))}
                    className="min-h-tap rounded-md border border-line px-3 text-sm"
                  >
                    {t('Edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAkanDihapus(tpl)}
                    className="min-h-tap rounded-md border border-line px-3 text-sm"
                  >
                    {t('Delete')}
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Tidak ada kamera yang menunjuk balik ke template (§G11.4), jadi
          kalimatnya bisa menjanjikan itu apa adanya — dan justru karena bisa,
          ia harus. Pagarnya sendiri sama dengan penghapusan lain (§G17). */}
      {akanDihapus && (
        <DialogHapus
          nama={akanDihapus.name}
          judul={t('Delete template {nama}', { nama: akanDihapus.name })}
          keterangan={t(
            'No camera is changed — templates are only used when a camera is created.',
          )}
          onBatal={() => setAkanDihapus(null)}
          onHapus={async (k) => {
            await deleteTemplate(akanDihapus.id, k);
            setAkanDihapus(null);
            await muat();
          }}
        />
      )}

      {bolehUbah && (
        <button
          type="button"
          onClick={() => setDraft(draftBaru())}
          className="mt-4 min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast"
        >
          {t('New template')}
        </button>
      )}
    </div>
  );
}
